import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { I18nUtils } from '@medable/i18n-core';
import { useFeatureFlags } from 'libs.flags.react';
import { LocaleContext, useLocalizeMessage, Link, Spinner, Text, useDateTimeFormat } from 'libs.nucleus';
import { ListInformation, useBooleanState } from 'libs.react';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { MIN_AXON_VERSION_FOR_EMAIL_UPDATE } from '../../constants/versions.constants';
import { OrgContext } from '../../contexts/org';
import { getParticipantNextVisitWindow, isParticipantAuthenticated, isParticipantDeactivated, isParticipantInactive, } from '../../helpers/participants.helpers';
import { getStudyShortCode } from '../../helpers/study.helpers';
import { meetsMinimumVersion } from '../../helpers/version.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
import { EParticipantInviteStatus } from '../../types/participants.type';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
import { capitalizeFirstLetter } from '../../utils';
import { ParticipantDeactivationModal } from '../participant_deactivation/participant_deactivation_modal.component';
import { ParticipantEmailChangeModal } from '../participant_email_change/participant_email_change_modal.component';
import { ParticipantEditFormModal } from '../participant_form/participant_edit_form_modal.component';
import { ParticipantInviteModal } from '../participant_form/participant_invite_modal.component';
import { ResetPassword } from '../reset_password';
export const ParticipantDetails = ({ participant, participantDetailsLoading }) => {
    const translate = useLocalizeMessage();
    const currentLocale = useContext(LocaleContext);
    const org = useOrgContext();
    const site = useSiteContext();
    const { participantStatusMap } = useContext(OrgContext);
    const queryClient = useQueryClient();
    const { formatDateTime, formatPlainDate } = useDateTimeFormat();
    const { environment } = useCurrentEnvironment();
    const { isUserPermitted } = useLumosAuth();
    const { isFeatureEnabled } = useFeatureFlags();
    const [isInviteModalOpen, setIsInviteModalOpen, setIsInviteModalClose] = useBooleanState(false);
    const [isDeactivationModalOpen, setIsDeactivationModalOpen, setIsDeactivationModalClosed] = useBooleanState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen, setIsResetPasswordModalClose] = useBooleanState(false);
    const [isParticipantModalOpen, setIsParticipantModalOpen, setIsParticipantModalClose] = useBooleanState(false);
    const [isEmailChangeModalOpen, setIsEmailChangeModalOpen, setIsEmailChangeModalClosed] = useBooleanState(false);
    const getStudyCode = async () => {
        if (!environment) {
            return null;
        }
        return await getStudyShortCode(environment);
    };
    const { data: studyCode } = useQuery({
        queryKey: ['getStudyCode', environment?.code],
        queryFn: getStudyCode,
        enabled: !queryClient.getQueryData(['getStudyCode', environment?.code]),
    });
    const isOrgNoPii = org?.c_no_pii === true;
    const isSiteNoPii = site?.c_no_pii === true;
    const hasMixedPIISupport = !isOrgNoPii && !isSiteNoPii;
    const isNoPIIStudy = isOrgNoPii || isSiteNoPii;
    if (!participant || participantDetailsLoading) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    const refreshDetailsData = () => {
        queryClient.invalidateQueries({
            queryKey: ['participantDetails', participant._id, currentLocale],
        });
    };
    const onDeactivationModalClose = () => {
        setIsDeactivationModalClosed();
        refreshDetailsData();
    };
    const onInviteModalClose = () => {
        setIsInviteModalClose();
        refreshDetailsData();
    };
    const onEmailChangeModalClose = () => {
        setIsEmailChangeModalClosed();
        refreshDetailsData();
    };
    //  If participant doesn't have an account or is deactivated or inactive or simply doest have the permissions, don't show the deactivate button
    const canDeactivateParticipant = isParticipantAuthenticated(participant) &&
        !isParticipantDeactivated(participant) &&
        !isParticipantInactive(participant) &&
        isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_DEACTIVATE);
    const canResetPassword = isParticipantAuthenticated(participant) && isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_RESET_PASSWORD);
    const canUpdateParticipant = !isParticipantAuthenticated(participant) && isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_UPDATE);
    // A registered participant's email is read-only inline; it changes through the confirmation flow, which needs
    // the Axon email-update route (SSP-4580) and the workspace's ParticipantEmailChange flag (SSP-4654): MoM
    // declares it off, a workspace turns it on by override, and a flag MoM does not declare evaluates off.
    // Keyed on the email having arrived: a blinded role receives none.
    // A participant with a username is shown by username (see the identifier rows below), so the action is not
    // offered even when an email is also on record — offering it there is a product decision, not taken here.
    const canChangeParticipantEmail = isParticipantAuthenticated(participant) &&
        Boolean(participant.c_email) &&
        !participant.c_username &&
        !isNoPIIStudy &&
        !isParticipantDeactivated(participant) &&
        !isParticipantInactive(participant) &&
        isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_UPDATE) &&
        meetsMinimumVersion({
            currentVersion: org?.axon__version,
            minimumVersion: MIN_AXON_VERSION_FOR_EMAIL_UPDATE,
        }) &&
        isFeatureEnabled(SITE_FEATURE_FLAGS.PARTICIPANT_EMAIL_CHANGE);
    // The two actions are exclusive: Edit needs an unregistered participant, Change (email) a registered one.
    const getEmailRowAction = () => {
        if (canUpdateParticipant) {
            return (_jsx(Link, { "data-testid": 'edit-participant', href: '#', onClick: setIsParticipantModalOpen, children: translate('generic.edit') }));
        }
        if (canChangeParticipantEmail) {
            return (_jsx(Link, { "data-testid": 'change-participant-email', href: '#', onClick: setIsEmailChangeModalOpen, children: translate('action.participant_email_change.change') }));
        }
        return undefined;
    };
    const emailRowAction = getEmailRowAction();
    const isR52OrHigher = org?.c_pinned_version !== undefined && org.c_pinned_version >= 50200;
    const detailItems = [
        {
            title: translate('generic.language'),
            description: participant.c_locale ? I18nUtils.getLanguageLabel(participant.c_locale, currentLocale, false) : null,
            dataTestId: 'participant-details-language',
        },
        // R5.2+ support when mixed PII is enabled, no email is provided and participant is not authenticated
        // Identifier rows hide themselves when blank unless the user can act on them: a blinded role receives neither value
        ...(isR52OrHigher && hasMixedPIISupport && !participant.c_email && !isParticipantAuthenticated(participant)
            ? [
                {
                    title: translate('generic.username'),
                    description: participant.c_username,
                    hideIfEmpty: true,
                    dataTestId: 'participant-details-username',
                },
                {
                    title: translate('generic.email'),
                    description: participant.c_email,
                    hideIfEmpty: !canUpdateParticipant,
                    dataTestId: 'participant-details-email',
                    ...(canUpdateParticipant && {
                        actionContent: (_jsx(Link, { "data-testid": 'edit-participant', href: '#', onClick: setIsParticipantModalOpen, children: translate('generic.edit') })),
                    }),
                },
            ]
            : // Pre R5.2 logic or handle registered participant with username or caregiver with username
                isNoPIIStudy || Boolean(participant.c_username)
                    ? [
                        {
                            title: translate('generic.username'),
                            description: participant.c_username,
                            hideIfEmpty: !canResetPassword,
                            ...(canResetPassword && {
                                actionContent: (_jsx(Link, { "data-testid": 'reset-password', href: '#', onClick: setIsResetPasswordModalOpen, children: translate('action.password_reset') })),
                            }),
                            dataTestId: 'participant-details-username',
                        },
                    ]
                    : [
                        {
                            title: translate('generic.email'),
                            description: participant.c_email,
                            hideIfEmpty: !canUpdateParticipant,
                            dataTestId: 'participant-details-email',
                            ...(emailRowAction && { actionContent: emailRowAction }),
                        },
                    ]),
        ...(!isParticipantDeactivated(participant) && !isParticipantInactive(participant)
            ? [
                {
                    title: translate('label.invite_code'),
                    description: participant.c_invite === EParticipantInviteStatus.INVITED ? (_jsxs("div", { children: [_jsxs(Text, { size: 'sm', className: 'text-text-primary', children: [translate('label.study_code'), ": ", studyCode] }), _jsxs(Text, { size: 'sm', className: 'text-text-primary', children: [translate('label.invite_code'), ": ", participant.c_access_code] })] })) : (_jsx(Text, { size: 'sm', children: participant.c_invite ? translate(capitalizeFirstLetter(participant.c_invite)) : '' })),
                    actionContent: isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_EMAIL_INVITE) ? (_jsx(Link, { "data-testid": 'add-view-invite', href: '#', onClick: setIsInviteModalOpen, children: [EParticipantInviteStatus.INVITED, EParticipantInviteStatus.ACCEPTED].includes(participant.c_invite)
                            ? translate('generic.view')
                            : translate('label.participant_details.new_invite') })) : undefined,
                    dataTestId: 'participant-details-invite-code',
                },
            ]
            : []),
        {
            title: translate('generic.cohort'),
            description: participant.c_visit_schedule?.c_name,
            dataTestId: 'participant-details-cohort',
        },
        {
            title: translate('generic.status'),
            description: participantStatusMap.get(participant.c_status) || participant.c_status,
            dataTestId: 'participant-details-status',
        },
        {
            title: translate('label.participant_details.next_visit'),
            description: getParticipantNextVisitWindow(participant, formatPlainDate, translate),
            dataTestId: 'participant-details-next-visit',
        },
        {
            title: translate('generic.create', { tense: 'past' }),
            description: formatDateTime(participant.created),
            ...(canDeactivateParticipant && {
                actionContent: (_jsx(Link, { "data-testid": 'deactivate-action', href: '#', onClick: setIsDeactivationModalOpen, children: translate('generic.deactivate') })),
            }),
            dataTestId: 'participant-details-created',
        },
    ];
    return (_jsxs(_Fragment, { children: [isInviteModalOpen && (_jsx(ParticipantInviteModal, { participant: participant, onClose: onInviteModalClose, onDone: onInviteModalClose })), _jsx(ParticipantDeactivationModal, { isModalOpen: isDeactivationModalOpen, participant: participant, onClose: onDeactivationModalClose, onDone: onDeactivationModalClose }), _jsx(ResetPassword, { isModalOpen: isResetPasswordModalOpen, onClose: setIsResetPasswordModalClose, participant: participant }), _jsx(ParticipantEditFormModal, { isModalOpen: isParticipantModalOpen, participant: participant, onClose: setIsParticipantModalClose }), _jsx(ParticipantEmailChangeModal, { isModalOpen: isEmailChangeModalOpen, participant: participant, onClose: onEmailChangeModalClose }), _jsx("div", { className: 'flex flex-col gap-8', children: _jsx(ListInformation, { dataTestId: 'participant-details-list-information', items: detailItems, useDefaultEmptyDescription: true }) })] }));
};
